import { types } from 'actions/organisation';
import { searchSuperOrganisationWorkItems } from 'services/api';
import { buildQuery } from 'utils/search';
import { searchId as id } from 'components/common';
import { handleSearchError } from '../notifications';
import { getHasValidItemSearchQuery } from './selectors';

// Activity (work-items) tab.
//
// SCIL-17744: Activity now reads its filter INPUTS from the shared `filters`
// slice (same as People; mutations live in researchers.js). Only Activity's
// results, paging, sort and its changed-filters flag live here.

// the counts are OpenSearch cardinalities, which are null over zero hits
const totalsFrom = res => ({
  emailCount: res.emailCount ?? 0,
  people: res.distinctSuperResearchersCount ?? 0,
  countries: res.countryCount ?? 0,
  activities: res.activityCount ?? 0,
});

export const workItemsReducers = {
  [types.SET_WORKITEM_PAGE_NUMBER]: (state, action) => {
    const { organisationId, pageNumber } = action.payload;
    return {
      ...state,
      workItemPageNumbers: {
        ...state.workItemPageNumbers,
        [organisationId]: pageNumber,
      },
    };
  },
  [types.SET_WORKITEM_PAGE_SIZE]: (state, action) => {
    const { organisationId, pageSize } = action.payload;
    return {
      ...state,
      workItemPageSize: {
        ...state.workItemPageSize,
        [organisationId]: pageSize,
      },
    };
  },
  [types.SET_WORKITEM_SORT]: (state, action) => {
    const { organisationId, sort } = action.payload;
    return {
      ...state,
      workItemSort: { ...state.workItemSort, [organisationId]: sort },
    };
  },
  [types.SET_WORKITEM_SEARCH_REQUEST]: (state, action) => {
    const { organisationId, searchRequest } = action.payload;
    if (action.payload.searchRequest?.error) {
      handleSearchError(action.payload.searchRequest.error);
    }
    if (searchRequest.res) {
      return {
        ...state,
        workItemTotals: {
          ...state.workItemTotals,
          [organisationId]: totalsFrom(searchRequest.res),
        },
        blockedForWorkItemExport: {
          ...state.blockedForWorkItemExport,
          [organisationId]: false,
        },
        workItems: {
          ...state.workItems,
          [organisationId]: searchRequest.res,
        },
        workItemHighlight: {
          ...state.workItemHighlight,
        },
        workItemLoading: { ...state.workItemLoading, [organisationId]: false },
        workItemSearchError: {
          ...state.workItemSearchError,
          [organisationId]: action.payload.searchRequest?.error,
        },
      };
    }
    return {
      ...state,
      workItemLoading: {
        ...state.workItemLoading,
        [organisationId]: searchRequest.loading ?? false,
      },
      workItemSearchError: {
        ...state.workItemSearchError,
        [organisationId]: action.payload.searchRequest?.error,
      },
      workItems: {},
      workItemTotals: {},
      workItemHighlight: {
        ...state.workItemHighlight,
        [organisationId]: action.payload.searchRequest?.error
          ? null
          : state.workItemHighlight[organisationId],
      },
      blockedForWorkItemExport: {
        ...state.blockedForWorkItemExport,
        [organisationId]: false,
      },
    };
  },
  [types.PERFORM_WORKITEM_SEARCH]: (state, action) => {
    const { organisationId, request } = action.payload;
    if (!getHasValidItemSearchQuery(state, organisationId)) {
      return {
        ...state,
        workItemChangedFilters: {
          ...state.workItemChangedFilters,
          [organisationId]: true,
        },
      };
    }

    const initialPageNumber = 0;

    const [query, highlightQuery, terms, nestedQueries] = buildQuery(
      state.filters[organisationId],
      null,
      null,
      true
    );

    const pageNumber = state.workItemChangedFilters[organisationId]
      ? initialPageNumber
      : state.workItemPageNumbers[organisationId] ?? initialPageNumber;

    // SCIL-17603: mirror main search — carry the AI session id under `sessionId`
    // when the shared slice holds one (stripped from the Lucene query by buildQuery).
    const aiSessionId = state.filters[organisationId]?.aiFilterSessionId;

    const cancelWorkItemsSearch = request({
      url: searchSuperOrganisationWorkItems(organisationId),
      body: {
        id,
        query: `${query}`,
        count: state.workItemPageSize[organisationId] ?? 50,
        from: (state.workItemPageSize[organisationId] ?? 50) * pageNumber,
        orderBy: state.workItemSort[organisationId]?.field || 'Date',
        order: state.workItemSort[organisationId]?.direction || 'desc',
        filters: terms,
        nestedQueries: nestedQueries,
        highlightQuery,
        ...(aiSessionId ? { sessionId: aiSessionId } : {}),
      },
    });

    return {
      ...state,
      workItemPageNumbers: {
        ...state.workItemPageNumbers,
        [organisationId]: state.workItemPageNumbers[organisationId],
      },
      workItemChangedFilters: {
        ...state.workItemChangedFilters,
        [organisationId]: false,
      },
      workItemHighlight: {
        ...state.workItemHighlight,
        [organisationId]: highlightQuery,
      },
      cancelWorkItemsSearch,
    };
  },
};
